import { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { Text } from '@/components/Text';
import { useWordsetContext } from '@/contexts/WordSetContext/useWordsetContext';
import { styles } from './styles';
import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import Checkbox from '@/components/ui/therapist/Checkbox';
import { IS_WEB } from '@/utils/platform';
import { PhonemeSelectionData } from './config';

type PhonemeKey = 'initial' | 'medial' | 'final';
type PhonemeSelectedKey = `${PhonemeKey}Selected`;

// Props are optional because TabsContainer injects them via React.cloneElement
type PhonemeSelectionProps = {
  data?: PhonemeSelectionData;
  keyName?: PhonemeKey;
};

export const PhonemeSelection = ({ data, keyName }: PhonemeSelectionProps) => {
  const {
    phonemes,
    changePhenomenonState,
    changePhenomenonRowState,
    changeAllPhenomenonState,
  } = useWordsetContext();

  // All hooks must run before any early return (Rules of Hooks)
  const selectedKey = `${keyName ?? 'initial'}Selected` as PhonemeSelectedKey;
  const activePhonemesSelected = phonemes[
    selectedKey as keyof typeof phonemes
  ] as string[];

  const allValues = useMemo(() => {
    if (!data) {
      return [];
    }
    const { Vokale, Plosive, Frikative, Nasale, Weitere } = data;
    return [...Vokale, ...Plosive, ...Frikative, ...Nasale, ...Weitere];
  }, [data]);

  const isAllCheckboxChecked = useMemo(() => {
    return activePhonemesSelected.length === allValues.length;
  }, [activePhonemesSelected.length, allValues.length]);

  const height = useResponsiveHeight(40);

  if (!data || !keyName) {
    return null;
  }

  const { title, selectAllText, Vokale, Plosive, Frikative, Nasale, Weitere } =
    data;

  const wrappedData = [
    { name: 'Vokale', values: Vokale },
    { name: 'Plosive', values: Plosive },
    { name: 'Frikative', values: Frikative },
    { name: 'Nasale', values: Nasale },
    { name: 'Weitere', values: Weitere },
  ];

  const handleAlleCheckboxPress = (
    name: PhonemeKey,
    values = allValues,
    isRemove: boolean,
  ) => {
    changeAllPhenomenonState?.(name, values, isRemove);
  };

  const phonemeCheckboxContainerPress = (
    name: PhonemeKey,
    values: string[],
    isRemove: boolean,
  ) => {
    changePhenomenonRowState?.(name, values, isRemove);
  };

  const phonemeCheckboxPress = (
    name: PhonemeKey,
    value: string,
    isRemove: boolean,
  ) => {
    changePhenomenonState?.(name, value, isRemove);
  };

  return (
    <View
      style={[
        styles.phonemauswahlContainer,
        { height, flex: IS_WEB ? undefined : 1 },
      ]}
    >
      <ScrollView>
        <Text style={styles.phonemauswahlTitle}>{title}</Text>
        <Checkbox
          isSelected={isAllCheckboxChecked}
          label={selectAllText}
          labelStyle={{ fontSize: 20, fontWeight: 'bold' }}
          onPress={() =>
            handleAlleCheckboxPress(keyName, allValues, isAllCheckboxChecked)
          }
        />
        <View style={styles.horizantalSeperator} />
        {wrappedData.map((sector, outerIndex) => {
          const { name, values } = sector;
          const isSectorContainerCheckboxChecked = (() => {
            let isChecked = false;
            for (let i = 0; i < values.length; i++) {
              const eachValue = values[i];
              if (activePhonemesSelected.includes(eachValue)) {
                isChecked = true;
              } else {
                isChecked = false;
                break;
              }
            }
            return isChecked;
          })();

          return (
            <View style={styles.sectorContainer} key={name}>
              <Checkbox
                isSelected={isSectorContainerCheckboxChecked}
                label={name}
                labelStyle={{ fontSize: 20, fontWeight: 'bold' }}
                onPress={() =>
                  phonemeCheckboxContainerPress(
                    keyName,
                    values,
                    isSectorContainerCheckboxChecked,
                  )
                }
              />
              <View style={styles.sectorContainerBody}>
                {values.map((value) => {
                  const isChecked = activePhonemesSelected.includes(value);
                  return (
                    <View key={value} style={styles.sectorContainerBodyItem}>
                      <Checkbox
                        isSelected={isChecked}
                        label={value}
                        labelStyle={{ fontSize: 20 }}
                        onPress={() =>
                          phonemeCheckboxPress(keyName, value, isChecked)
                        }
                      />
                    </View>
                  );
                })}
              </View>
              {outerIndex !== wrappedData.length - 1 ? (
                <View style={styles.horizantalSeperator} />
              ) : null}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
};
